import { useMemo } from 'react';

import { AppExperiments, useIsTreatment } from '../../../../utils/experiments';
import { useRoles } from '../../hooks/useRoles';

import { getNewlyAddedProtectedRoles } from './ProtectedRoleWarning';

function isAnyRole() {
  return true;
}

export type ProtectedRoleWarningInfo = {
  roleName: string;
  gracePeriodMs: number;
};

export type EntityRoleSelection = {
  legalEntityId: string;
  roles: readonly string[];
};

/**
 * Which protected role (if any) the current invite selection includes, for the
 * summary `ProtectedRoleWarning` in the invite trays. Each role is resolved
 * against the definitions of the entity it was selected on, since protection
 * can differ by entity. Picks the longest grace period, since one warning
 * covers every selected protected role and entity. Every selected role is new
 * for an invite.
 *
 * Returns null in control (`t0018_4fba688a`) so the trays render as today.
 */
export function useProtectedRoleWarning(
  selections: readonly EntityRoleSelection[],
): ProtectedRoleWarningInfo | null {
  const isGracePeriodTreatment = useIsTreatment(AppExperiments.t0018_4fba688a);
  const { rolesByProductPerEntity } = useRoles();

  return useMemo(() => {
    if (!isGracePeriodTreatment) return null;

    let longest: ProtectedRoleWarningInfo | null = null;
    for (const { legalEntityId, roles } of selections) {
      const entityRoles = Object.values(rolesByProductPerEntity[legalEntityId] ?? {}).flat();
      for (const role of roles) {
        const definition = entityRoles.find(({ value }) => value === role);
        const gracePeriodMs = definition?.protection?.gracePeriodMs ?? 0;
        if (definition && gracePeriodMs > (longest?.gracePeriodMs ?? 0)) {
          longest = { roleName: definition.name, gracePeriodMs };
        }
      }
    }
    return longest;
  }, [isGracePeriodTreatment, rolesByProductPerEntity, selections]);
}

/**
 * IDFE-4025: the edit-tray variant. Only protected roles the member doesn't
 * already hold (or have pending) on that entity count, so re-saving a held or
 * pending Admin doesn't warn. Both maps are keyed by legal entity ID:
 * `selectedByEntity` is the tray's current selection, `currentByEntity` the
 * member's roles from the API. Null in control.
 */
export function useEditProtectedRoleWarning(
  selectedByEntity: Record<string, readonly string[]>,
  currentByEntity: Record<string, readonly string[]>,
): ProtectedRoleWarningInfo | null {
  // Every newly added role passes through; useProtectedRoleWarning resolves
  // protection per entity, since it can differ by entity.
  const newlyAddedSelections = useMemo(() => {
    const rolesByEntity = new Map<string, string[]>();
    for (const { legalEntityId, role } of getNewlyAddedProtectedRoles(
      selectedByEntity,
      currentByEntity,
      isAnyRole,
    )) {
      rolesByEntity.set(legalEntityId, [...(rolesByEntity.get(legalEntityId) ?? []), role]);
    }
    return [...rolesByEntity].map(([legalEntityId, roles]) => ({ legalEntityId, roles }));
  }, [currentByEntity, selectedByEntity]);

  return useProtectedRoleWarning(newlyAddedSelections);
}
